/**
 * @file Boutons de navigation (précédent / suivant / confirmer) du widget de réservation.
 * @module packages.common.components.table-reservation.components.StepActions
 *
 * Affiche un couple de boutons alignés horizontalement. Le bouton "suivant" change
 * de libellé et d'icône quand l'utilisateur atteint la dernière étape (confirmation),
 * et affiche un spinner pendant le chargement de la soumission.
 */

import React from 'react';
import PropTypes from 'prop-types';

// @mui
import {styled} from '@mui/material/styles';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';

// icons
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

// ----------------------------------------------------------------------

const RootStyle = styled(Box)(({theme}) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: 0,
}));

// ----------------------------------------------------------------------

/**
 * Barre d'actions précédent/suivant pour le stepper de réservation.
 *
 * @param {Object} props
 * @param {boolean} [props.showBack=false] - Affiche le bouton retour
 * @param {Function} [props.onBack] - Callback du bouton retour
 * @param {Function} props.onNext - Callback du bouton suivant
 * @param {boolean} [props.isNextLoading=false] - Affiche un spinner sur le bouton suivant
 * @param {boolean} [props.isLastStep=false] - Dernière étape : change l'icône et le libellé
 * @param {boolean} [props.disableNext=false] - Désactive le bouton suivant
 * @param {string} [props.nextLabel] - Libellé personnalisé du bouton suivant
 * @param {Function} props.t - Fonction de traduction générique
 * @return {JSX.Element}
 */
export function StepActions({
  showBack = false,
  onBack,
  onNext,
  isNextLoading = false,
  isLastStep = false,
  disableNext = false,
  nextLabel,
  t,
}) {
  const nextButtonText = nextLabel ||
    (isLastStep ? t('common.buttons.confirm') : t('common.buttons.next'));
  const backButtonText = t('common.buttons.previous');

  return (
    <RootStyle>
      <Box>
        {showBack && (
          <Button
            color="inherit"
            onClick={onBack}
            startIcon={<ArrowBackIcon aria-hidden="true" />}
            disabled={isNextLoading}
            size="medium"
            aria-label={backButtonText}
          >
            {backButtonText}
          </Button>
        )}
      </Box>

      <Box>
        <Button
          variant="contained"
          onClick={onNext}
          disabled={isNextLoading || disableNext}
          aria-busy={isNextLoading}
          aria-label={nextButtonText}
          endIcon={
            isNextLoading ? (
              <CircularProgress size={16} color="inherit" aria-hidden="true" />
            ) : isLastStep ? (
              <CheckCircleIcon fontSize="small" aria-hidden="true" />
            ) : (
              <ArrowForwardIcon fontSize="small" aria-hidden="true" />
            )
          }
          size="medium"
        >
          {nextButtonText}
        </Button>
      </Box>
    </RootStyle>
  );
}

StepActions.propTypes = {
  showBack: PropTypes.bool,
  onBack: PropTypes.func,
  onNext: PropTypes.func.isRequired,
  isNextLoading: PropTypes.bool,
  isLastStep: PropTypes.bool,
  disableNext: PropTypes.bool,
  nextLabel: PropTypes.string,
  t: PropTypes.func.isRequired,
};
